import type { ICampaignHostCreationAccess } from '../types/global/campaignHostSubscription.types';
import { formatUsdCents } from './campaignHostSubscriptionFormat';

export const CAMPAIGN_CREATION_WALLET_SHORTFALL = 'CAMPAIGN_CREATION_WALLET_SHORTFALL';
export const CAMPAIGN_CREATOR_LIMIT = 'CAMPAIGN_CREATOR_LIMIT';

/** Effective creators-per-campaign cap; `null` = unlimited / unknown. */
export function getCreationAccessMaxCreators(access: ICampaignHostCreationAccess | null | undefined): number | null {
    if (!access) return null;
    if (access.max_creators_unlimited) return null;
    if (access.max_creators == null) return null;
    const n = Number(access.max_creators);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.floor(n);
}

export function formatCreationAccessMaxCreatorsLabel(
    access: ICampaignHostCreationAccess | null | undefined,
): string | null {
    if (!access) return null;
    if (access.max_creators_unlimited) return 'Unlimited creators per campaign on your plan.';
    const max = getCreationAccessMaxCreators(access);
    if (max == null) return null;
    return `Your plan allows up to ${max} creators per campaign.`;
}

export function clampCreatorsToPlanLimit(requested: number, maxCreators: number | null): number {
    const n = Math.max(1, Math.floor(requested) || 1);
    if (maxCreators == null) return n;
    return Math.min(n, maxCreators);
}

export function isOverCreatorPlanLimit(requested: number, maxCreators: number | null): boolean {
    if (maxCreators == null) return false;
    return requested > maxCreators;
}

export function getCreationBillingCopy(access: ICampaignHostCreationAccess | null | undefined): string | null {
    if (!access) return null;
    if (access.unlimited) return 'Unlimited campaign creation included.';
    const fee = formatUsdCents(access.per_campaign_fee_cents);
    const shortfall = Number(access.shortfall_cents ?? 0);
    if (access.requires_wallet_charge && shortfall > 0) {
        return `Add ${formatUsdCents(shortfall)} to your campaign wallet, or subscribe for unlimited creation.`;
    }
    if (access.requires_wallet_charge) {
        return `Creating this campaign charges ${fee} from your campaign wallet.`;
    }
    return null;
}

export function creationAccessBlocksCreate(access: ICampaignHostCreationAccess | null | undefined): boolean {
    if (!access) return false;
    if (access.can_create === false) return true;
    if (!access.requires_wallet_charge) return false;
    return Number(access.shortfall_cents ?? 0) > 0;
}

type ApiErrorLike = {
    status?: number;
    data?: {
        code?: string;
        message?: string;
        max_creators?: number;
        requested_creators?: number;
        shortfall_cents?: number;
        per_campaign_fee_cents?: number;
        wallet_balance_cents?: number;
        data?: Record<string, unknown>;
    };
};

function readErrorBody(error: unknown): ApiErrorLike['data'] | undefined {
    const err = error as ApiErrorLike;
    const body = err?.data;
    if (!body || typeof body !== 'object') return undefined;
    const nested = body.data;
    if (nested && typeof nested === 'object' && (nested.code || body.code)) {
        return { ...body, ...nested } as ApiErrorLike['data'];
    }
    return body;
}

export function parseCampaignCreationEntitlementError(error: unknown): {
    code: typeof CAMPAIGN_CREATION_WALLET_SHORTFALL | typeof CAMPAIGN_CREATOR_LIMIT | null;
    message: string;
    max_creators?: number;
    requested_creators?: number;
    shortfall_cents?: number;
} {
    const err = error as ApiErrorLike;
    const body = readErrorBody(error);
    const code = typeof body?.code === 'string' ? body.code : null;
    const message =
        (typeof body?.message === 'string' && body.message) ||
        (err?.status === 402
            ? 'Insufficient campaign wallet balance to create this campaign.'
            : err?.status === 403
            ? 'Creator limit exceeded for your plan.'
            : 'Request failed');

    if (code === CAMPAIGN_CREATION_WALLET_SHORTFALL || err?.status === 402) {
        return {
            code: CAMPAIGN_CREATION_WALLET_SHORTFALL,
            message,
            shortfall_cents: typeof body?.shortfall_cents === 'number' ? body.shortfall_cents : undefined,
        };
    }
    if (code === CAMPAIGN_CREATOR_LIMIT || (err?.status === 403 && body?.max_creators != null)) {
        return {
            code: CAMPAIGN_CREATOR_LIMIT,
            message,
            max_creators: typeof body?.max_creators === 'number' ? body.max_creators : undefined,
            requested_creators: typeof body?.requested_creators === 'number' ? body.requested_creators : undefined,
        };
    }
    return { code: null, message };
}
